<template>
  <div class="columns profile--settings">
    <form @submit.prevent="updateUser('profile')">
      <div class="small-12 row profile--settings--row">
        <div class="columns small-3">
          <h4 class="block-title">
            {{ $t('PROFILE_SETTINGS.FORM.PROFILE_SECTION.TITLE') }}
          </h4>
          <p>{{ $t('PROFILE_SETTINGS.FORM.PROFILE_SECTION.NOTE') }}</p>
        </div>
        <div class="columns small-9 medium-5">
          <woot-avatar-uploader
            :label="$t('PROFILE_SETTINGS.FORM.PROFILE_IMAGE.LABEL')"
            :src="avatarUrl"
            @change="handleImageUpload"
          />
          <label :class="{ error: $v.name.$error }">
            {{ $t('PROFILE_SETTINGS.FORM.NAME.LABEL') }}
            <input
              v-model="name"
              type="text"
              :placeholder="$t('PROFILE_SETTINGS.FORM.NAME.PLACEHOLDER')"
              @input="$v.name.$touch"
            />
            <span v-if="$v.name.$error" class="message">
              {{ $t('PROFILE_SETTINGS.FORM.NAME.ERROR') }}
            </span>
          </label>
          <label :class="{ error: $v.displayName.$error }">
            {{ $t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.LABEL') }}
            <input
              v-model="displayName"
              type="text"
              :placeholder="
                $t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.PLACEHOLDER')
              "
              @input="$v.displayName.$touch"
            />
          </label>
          <label :class="{ error: $v.email.$error }">
            {{ $t('PROFILE_SETTINGS.FORM.EMAIL.LABEL') }}
            <input
              v-model.trim="email"
              type="email"
              :placeholder="$t('PROFILE_SETTINGS.FORM.EMAIL.PLACEHOLDER')"
              @input="$v.email.$touch"
            />
            <span v-if="$v.email.$error" class="message">
              {{ $t('PROFILE_SETTINGS.FORM.EMAIL.ERROR') }}
            </span>
          </label>
          <label>
            {{ $t('PROFILE_SETTINGS.FORM.SIGNATURE.LABEL') }}
            <input
                v-model.trim="signature"
                type="text"
                :placeholder="$t('PROFILE_SETTINGS.FORM.SIGNATURE.PLACEHOLDER')"
            />
          </label>
          <label>
            {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
            <select v-model="availability">
              <option
                v-for="status in availabilityStatuses"
                :key="status.key"
                class="text-capitalize"
                :value="status.value"
              >
                {{ status.label }}
              </option>
            </select>
          </label>
          <woot-button type="submit" :is-loading="isProfileUpdating">
            {{ $t('PROFILE_SETTINGS.BTN_TEXT') }}
          </woot-button>
        </div>
      </div>
    </form>
    <change-password />
      <div class="profile--settings--row row">
          <div class="columns small-3">
              <h4 class="block-title">
                  Camera & Microphone
              </h4>
              <p>Select Preferred Devices</p>
          </div>

          <div class="columns small-9 medium-5">
              <CameraAndMicrophoneSettings/>
          </div>
      </div>
      <div class="profile--settings--row row">
          <div class="columns small-3">
              <h4 class="block-title">
                  Default Conversations Tabs
              </h4>
              <p>Select default assignee type and status type</p>
          </div>

          <div class="columns small-9 medium-5">
              <DefaultTabs/>
          </div>
      </div>
    <notification-settings />
    <div class="profile--settings--row row">
      <div class="columns small-3">
        <h4 class="block-title">
          {{ $t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.TITLE') }}
        </h4>
        <p>{{ $t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.NOTE') }}</p>
      </div>
      <div class="columns small-9 medium-5">
        <woot-code :script="currentUser.access_token"></woot-code>
      </div>
    </div>
    <div class="profile--settings--row row">
      <div class="columns small-3">
        <h4 class="block-title">
          SIP Login Details
        </h4>
        <p>SIP Login Details can be used to connect third-party SIP devices to make calls outside the browser</p>
      </div>
      <div class="columns small-9 medium-5">
        <div>
          <h6> Websocket URI: </h6>
          <woot-code :script="'wss://aeonian-conversate.sip.signalwire.com'"></woot-code>
        </div>
        <div>
          <h6> Registar Server: </h6>
          <woot-code :script="'aeonian-conversate.sip.signalwire.com'"></woot-code>
        </div>
        <div>
          <table>
            <thead>
              <tr>
                <th>SIP Username</th>
                <th>SIP Password</th>
                <th>Inbox Name</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sipEndpoint in currentUser.sip_endpoints">
                <td>
                  {{ sipEndpoint.username }}
                </td>
                <td>
                  {{ sipEndpoint.password }}
                </td>
                <td>
                  {{ sipEndpoint.inbox_name }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { required, minLength, email } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
import { clearCookiesOnLogout } from '../../../../store/utils/api';
import NotificationSettings from './NotificationSettings';
import alertMixin from 'shared/mixins/alertMixin';
import ChangePassword from './ChangePassword.vue';
import ForwardSettings from "./ForwardSettings";
import CameraAndMicrophoneSettings
    from "../../../../components/CameraAndMicrophoneSettings/CameraAndMicrophoneSettings";
import DefaultTabs from "../../../../components/DefaultTabs";

const AVAILABILITY_STATUS_KEYS = ['online', 'busy', 'offline'];
export default {
  components: {
    ForwardSettings,
    NotificationSettings,
    ChangePassword,
    CameraAndMicrophoneSettings,
    DefaultTabs
  },
  mixins: [alertMixin],
  data() {
    return {
      avatarFile: '',
      avatarUrl: '',
      name: '',
      displayName: '',
      email: '',
      isProfileUpdating: false,
      errorMessage: '',
      signature: '',
      availability: 'online',
    };
  },
  validations: {
    name: {
      required,
      minLength: minLength(1),
    },
    displayName: {},
    email: {
      required,
      email,
    },
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentUserId: 'getCurrentUserID',
      currentAvailabilityStatus: 'getCurrentUserAvailability',
      records: 'getCodes',
    }),
    availabilityStatuses() {
      const originStatues = this.$t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUSES_LIST').map(
      (statusLabel, index) => ({
        label: statusLabel,
        value: AVAILABILITY_STATUS_KEYS[index],
      })
    );
      const customStatues = this.records.map(
        status => ({
          value: status.title,
          label: status.title,
        })
      );
      const statuses = [...originStatues, ...customStatues];
      return statuses;
    },
  },
  watch: {
    currentUserId(newCurrentUserId, prevCurrentUserId) {
      if (prevCurrentUserId !== newCurrentUserId) {
        this.initializeUser();
      }
    },
    currentAvailabilityStatus(newStatus, oldStatus) {
      if (newStatus !== oldStatus) {
        this.availability = newStatus;
      }
    },
  },
  mounted() {
    if (this.currentUserId) {
      this.initializeUser();
    }
  },
  methods: {
    initializeUser() {
      this.name = this.currentUser.name;
      this.email = this.currentUser.email;
      this.avatarUrl = this.currentUser.avatar_url;
      this.availability = this.currentUser.availability_status;
      this.displayName = this.currentUser.display_name;
      this.signature = this.currentUser.signature;
    },
    async updateUser() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.showAlert(this.$t('PROFILE_SETTINGS.FORM.ERROR'));
        return;
      }

      this.isProfileUpdating = true;
      const hasEmailChanged = this.currentUser.email !== this.email;
      try {
        await this.$store.dispatch('updateProfile', {
          name: this.name,
          email: this.email,
          avatar: this.avatarFile,
          displayName: this.displayName,
          availability: this.availability,
          signature: this.signature,
        });
        this.isProfileUpdating = false;
        if (hasEmailChanged) {
          console.log("LogoutLog: updateUser function");
          clearCookiesOnLogout();
          this.errorMessage = this.$t('PROFILE_SETTINGS.AFTER_EMAIL_CHANGED');
        }
        this.errorMessage = this.$t('PROFILE_SETTINGS.UPDATE_SUCCESS');
      } catch (error) {
        this.errorMessage = this.$t('RESET_PASSWORD.API.ERROR_MESSAGE');
        if (error?.response?.data?.error) {
          this.errorMessage = error.response.data.error;
        }
      } finally {
        this.isProfileUpdating = false;
        this.showAlert(this.errorMessage);
      }
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
  },
};
</script>

<style lang="scss">
@import '~dashboard/assets/scss/variables.scss';
@import '~dashboard/assets/scss/mixins.scss';

.profile--settings {
  padding: 24px;
  overflow: auto;
}

.profile--settings--row {
  @include border-normal-bottom;
  padding: $space-normal;
  .small-3 {
    padding: $space-normal $space-medium $space-normal 0;
  }
  .small-9 {
    padding: $space-normal;
  }
}
</style>
